import React, { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CalendarX2, RefreshCcw } from 'lucide-react';
import { useAccessMode } from '@/context/AccessModeContext';
import {
    SmartScheduleCoach,
    SmartScheduleEvent,
    SmartSchedulePeriod,
    countVisibleSmartScheduleEvents,
    getVisibleSmartScheduleCountBreakdown,
    toSmartScheduleKst,
} from '@/features/smartSchedule/model';
import { CoachColorProvider } from './CoachColorContext';
import DayView from './DayView';
import { SmartSchedulePreviewProvider } from './EventCard';
import MonthView from './MonthView';
import SmartScheduleToolbar from './SmartScheduleToolbar';
import WeekView from './WeekView';
import {
    DEFAULT_FILTERS,
    getEventEnd,
    getEventStart,
    getDefaultCreateTime,
    getPeriodRange,
    filterEvents,
    SmartScheduleFilters,
    SmartStatusFilter,
    SmartTypeFilter,
} from './utils';
import './SmartScheduleView.css';

export type SmartScheduleViewProps = {
    viewSwitcher?: React.ReactNode;
    centerId?: string;
    period: SmartSchedulePeriod;
    selectedDay: any;
    coaches: SmartScheduleCoach[];
    events: SmartScheduleEvent[];
    isLoading: boolean;
    isFetching?: boolean;
    errorMessage?: string;
    warningMessage?: string;
    onRetry?: () => void;
    onPeriodChange: (period: SmartSchedulePeriod) => void;
    onSelectedDayChange: (date: any) => void;
    onOpenEvent: (event: SmartScheduleEvent) => void;
    onCreateSlot: (slot: { date: string; startTime: string; coachId?: string; coachName?: string }) => void;
    onOpenCoachOrder?: () => void;
};

type StoredSettings = {
    filters?: SmartScheduleFilters;
};

const STORAGE_PREFIX = 'rappo:smart-schedule:v1';
const VALID_TYPES = new Set<SmartTypeFilter>(['all', 'private', 'group', 'other']);
const VALID_STATUSES = new Set<SmartStatusFilter>(['all', 'upcoming', 'ongoing', 'completed', 'pending', 'noshow', 'off']);

const storageKeyFor = (centerId?: string) => `${STORAGE_PREFIX}:${centerId || 'default'}`;

const readStoredSettings = (storageKey: string): StoredSettings => {
    if (typeof window === 'undefined') return {};
    try {
        const parsed = JSON.parse(window.localStorage.getItem(storageKey) || '{}') as StoredSettings;
        const storedFilters = parsed.filters;
        const rawStoredCoachIds = storedFilters?.coachIds;
        const storedCoachIds = Array.isArray(rawStoredCoachIds)
            ? rawStoredCoachIds.filter((id): id is string => typeof id === 'string')
            : [];
        const storedCoachMode: SmartScheduleFilters['coachMode'] =
            storedFilters?.coachMode === 'all' || storedFilters?.coachMode === 'custom'
                ? storedFilters.coachMode
                : storedCoachIds.length > 0
                ? 'custom'
                : 'all';
        return {
            filters: storedFilters
                ? {
                      type: VALID_TYPES.has(storedFilters.type) ? storedFilters.type : DEFAULT_FILTERS.type,
                      status: VALID_STATUSES.has(storedFilters.status) ? storedFilters.status : DEFAULT_FILTERS.status,
                      coachMode: storedCoachMode,
                      coachIds: storedCoachIds,
                      query: typeof storedFilters.query === 'string' ? storedFilters.query : '',
                  }
                : undefined,
        };
    } catch {
        return {};
    }
};

const SmartScheduleView = ({
    viewSwitcher,
    centerId,
    period,
    selectedDay,
    coaches,
    events,
    isLoading,
    isFetching = false,
    errorMessage,
    warningMessage,
    onRetry,
    onPeriodChange,
    onSelectedDayChange,
    onOpenEvent,
    onCreateSlot,
    onOpenCoachOrder,
}: SmartScheduleViewProps) => {
    const { isReadOnly } = useAccessMode();
    const storageKey = storageKeyFor(centerId);
    const initialSettings = useMemo(() => readStoredSettings(storageKey), []); // eslint-disable-line react-hooks/exhaustive-deps
    const [filters, setFilters] = useState<SmartScheduleFilters>(initialSettings.filters || DEFAULT_FILTERS);
    const [hydratedStorageKey, setHydratedStorageKey] = useState(storageKey);
    const [statusNow, setStatusNow] = useState(toSmartScheduleKst(new Date()));
    const onOpenEventRef = useRef(onOpenEvent);
    const onCreateSlotRef = useRef(onCreateSlot);
    const onPeriodChangeRef = useRef(onPeriodChange);
    const onSelectedDayChangeRef = useRef(onSelectedDayChange);
    onOpenEventRef.current = onOpenEvent;
    onCreateSlotRef.current = onCreateSlot;
    onPeriodChangeRef.current = onPeriodChange;
    onSelectedDayChangeRef.current = onSelectedDayChange;
    const handleOpenEvent = useCallback((event: SmartScheduleEvent) => onOpenEventRef.current(event), []);
    const handleCreateSlot = useCallback(
        (slot: { date: string; startTime: string; coachId?: string; coachName?: string }) =>
            onCreateSlotRef.current(slot),
        []
    );
    const handlePeriodChange = useCallback(
        (nextPeriod: SmartSchedulePeriod) => onPeriodChangeRef.current(nextPeriod),
        []
    );
    const handleSelectedDayChange = useCallback(
        (date: any) => onSelectedDayChangeRef.current(date),
        []
    );
    const effectiveFilters = useMemo(
        () =>
            period !== 'day' && filters.status === 'off'
                ? { ...filters, status: DEFAULT_FILTERS.status }
                : filters,
        [filters, period]
    );
    const deferredFilters = useDeferredValue(effectiveFilters);

    useEffect(() => {
        const stored = readStoredSettings(storageKey);
        setFilters(stored.filters || DEFAULT_FILTERS);
        setHydratedStorageKey(storageKey);
    }, [storageKey]);

    useEffect(() => {
        if (typeof window === 'undefined' || hydratedStorageKey !== storageKey) return;
        const timer = window.setTimeout(() => {
            try {
                const settings: StoredSettings = { filters };
                window.localStorage.setItem(storageKey, JSON.stringify(settings));
            } catch {
                // A full/blocked localStorage must never make the schedule unusable.
            }
        }, 250);
        return () => window.clearTimeout(timer);
    }, [filters, hydratedStorageKey, storageKey]);

    useEffect(() => {
        const timer = window.setInterval(() => setStatusNow(toSmartScheduleKst(new Date())), 60 * 1000);
        return () => window.clearInterval(timer);
    }, []);

    useEffect(() => {
        if (period === 'day') return;
        setFilters((current) =>
            current.status === 'off' ? { ...current, status: DEFAULT_FILTERS.status } : current
        );
    }, [period, storageKey]);

    const sortedCoaches = useMemo(
        () =>
            [...coaches].sort(
                (left, right) =>
                    (left.order ?? Number.MAX_SAFE_INTEGER) - (right.order ?? Number.MAX_SAFE_INTEGER) ||
                    left.name.localeCompare(right.name, 'ko-KR')
            ),
        [coaches]
    );
    const safeSelectedDay = useMemo(() => {
        const parsed = toSmartScheduleKst(selectedDay);
        return parsed.isValid() ? parsed : toSmartScheduleKst(new Date());
    }, [selectedDay]);
    const coachColorIds = useMemo(() => sortedCoaches.map((coach) => coach.id), [sortedCoaches]);
    const coachIdSignature = coachColorIds.join('|');

    useEffect(() => {
        if (sortedCoaches.length === 0) return;
        setFilters((current) => {
            if (current.coachMode === 'all') {
                return current.coachIds.length === 0 ? current : { ...current, coachIds: [] };
            }
            if (current.coachIds.length === 0) return current;
            const validIds = current.coachIds.filter((id) => sortedCoaches.some((coach) => coach.id === id));
            if (validIds.length === 0) return { ...current, coachMode: 'all', coachIds: [] };
            const allCoachesRemainSelected = validIds.length === sortedCoaches.length;
            const normalizedIds = allCoachesRemainSelected ? [] : validIds;
            const normalizedMode: SmartScheduleFilters['coachMode'] = allCoachesRemainSelected ? 'all' : 'custom';
            if (
                normalizedMode === current.coachMode &&
                normalizedIds.length === current.coachIds.length &&
                normalizedIds.every((id, index) => id === current.coachIds[index])
            ) {
                return current;
            }
            return { ...current, coachMode: normalizedMode, coachIds: normalizedIds };
        });
        // The signature makes roster changes observable without depending on a recreated array.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [coachIdSignature]);
    const visibleRange = useMemo(() => getPeriodRange(period, safeSelectedDay), [period, safeSelectedDay]);
    const periodEvents = useMemo(
        () =>
            events.filter(
                (event) =>
                    getEventStart(event).isBefore(visibleRange.end) &&
                    getEventEnd(event).isAfter(visibleRange.start)
            ),
        [events, visibleRange]
    );
    const displayablePeriodEvents = useMemo(
        () => (period === 'day' ? periodEvents : periodEvents.filter((event) => event.kind !== 'off')),
        [period, periodEvents]
    );
    const filterStatusNow = deferredFilters.status === 'all' ? undefined : statusNow;
    const filteredEvents = useMemo(
        () => filterEvents(displayablePeriodEvents, deferredFilters, sortedCoaches, filterStatusNow),
        [deferredFilters, displayablePeriodEvents, filterStatusNow, sortedCoaches]
    );
    // 월간 그리드는 선택한 달의 앞뒤 주까지 함께 그린다(2026-08 → 7/27~9/6, 42일).
    // 달력 칸에는 그대로 보여주되(앞뒤 달은 흐리게 표시), 상단 총계는 선택한 달만 센다.
    // filteredEvents 로 세면 "8월"을 골랐는데 7월 말·9월 초 일정까지 합산돼
    // 다른 화면의 월간 수치와 크게 어긋난다.
    const countedEvents = useMemo(() => {
        if (period !== 'month') return filteredEvents;
        const selected = toSmartScheduleKst(safeSelectedDay);
        const monthStart = selected.startOf('month');
        const monthEnd = selected.endOf('month');
        return filteredEvents.filter((event) => {
            const start = getEventStart(event);
            return !start.isBefore(monthStart) && !start.isAfter(monthEnd);
        });
    }, [filteredEvents, period, safeSelectedDay]);
    const filteredScheduleCount = useMemo(
        () => countVisibleSmartScheduleEvents(countedEvents),
        [countedEvents]
    );
    const filteredScheduleCountBreakdown = useMemo(
        () => getVisibleSmartScheduleCountBreakdown(countedEvents),
        [countedEvents]
    );
    const visibleCoaches = useMemo(
        () =>
            effectiveFilters.coachMode === 'all'
                ? sortedCoaches
                : sortedCoaches.filter((coach) => effectiveFilters.coachIds.includes(coach.id)),
        [effectiveFilters.coachIds, effectiveFilters.coachMode, sortedCoaches]
    );
    const hasActiveFilter =
        effectiveFilters.type !== 'all' ||
        effectiveFilters.status !== 'all' ||
        effectiveFilters.coachMode === 'custom' ||
        Boolean(effectiveFilters.query.trim());
    const hasNoCoachSelection =
        effectiveFilters.coachMode === 'custom' && effectiveFilters.coachIds.length === 0;
    const showEmptyNote =
        !isLoading &&
        !isFetching &&
        !errorMessage &&
        (period !== 'day' || hasNoCoachSelection) &&
        filteredEvents.length === 0;
    const content = (() => {
        if (errorMessage) {
            return (
                <div className="smart-schedule__state smart-schedule__state--error" role="alert">
                    <span><AlertTriangle size={23} aria-hidden="true" /></span>
                    <div>
                        <strong>일정을 불러오지 못했어요</strong>
                        <p>{errorMessage}</p>
                    </div>
                    {onRetry && (
                        <button type="button" onClick={onRetry}>
                            <RefreshCcw size={15} aria-hidden="true" /> 다시 시도
                        </button>
                    )}
                </div>
            );
        }

        if (isLoading) {
            return (
                <div className="smart-schedule__skeleton" role="status" aria-live="polite">
                    <span className="smart-schedule__sr-only">일정을 불러오는 중입니다.</span>
                    {Array.from({ length: 7 }, (_, index) => <i key={index} />)}
                </div>
            );
        }

        if (period === 'month') {
            return (
                <MonthView
                    selectedDay={safeSelectedDay}
                    now={statusNow}
                    events={filteredEvents}
                    onSelectedDayChange={handleSelectedDayChange}
                    onPeriodChange={handlePeriodChange}
                    onOpenEvent={handleOpenEvent}
                />
            );
        }
        if (period === 'week') {
            return (
                <WeekView
                    selectedDay={safeSelectedDay}
                    now={statusNow}
                    events={filteredEvents}
                    onSelectedDayChange={handleSelectedDayChange}
                    onPeriodChange={handlePeriodChange}
                    onOpenEvent={handleOpenEvent}
                />
            );
        }
        return (
            <DayView
                selectedDay={safeSelectedDay}
                now={statusNow}
                coaches={visibleCoaches}
                events={filteredEvents}
                availabilityEvents={periodEvents}
                onOpenEvent={handleOpenEvent}
                onCreateSlot={handleCreateSlot}
            />
        );
    })();

    return (
        <CoachColorProvider coachIds={coachColorIds}>
            <SmartSchedulePreviewProvider>
                <div className={`smart-schedule smart-schedule--${period}${isReadOnly ? ' smart-schedule--center-view' : ''}`}>
            <SmartScheduleToolbar
                viewSwitcher={viewSwitcher}
                period={period}
                selectedDay={safeSelectedDay}
                coaches={sortedCoaches}
                filters={effectiveFilters}
                filteredCount={filteredScheduleCount}
                filteredCountBreakdown={filteredScheduleCountBreakdown}
                onFiltersChange={setFilters}
                onPeriodChange={handlePeriodChange}
                onSelectedDayChange={handleSelectedDayChange}
                onCreateSlot={handleCreateSlot}
                onOpenCoachOrder={isReadOnly ? undefined : onOpenCoachOrder}
            />
            {warningMessage && !errorMessage && (
                <div className="smart-schedule__warning" role="status">
                    <AlertTriangle size={16} aria-hidden="true" />
                    <span>{warningMessage}</span>
                    {onRetry && <button type="button" onClick={onRetry}>다시 불러오기</button>}
                </div>
            )}
            {isFetching && !isLoading && (
                <div className="smart-schedule__fetching" role="status" aria-live="polite">
                    <span />
                    <em>최신 일정으로 업데이트 중</em>
                </div>
            )}
            <div className={`smart-schedule__content smart-schedule__content--${period}`}>
                {content}
                {showEmptyNote && (
                    <div className="smart-schedule__empty-note" role="status">
                        <span><CalendarX2 size={18} aria-hidden="true" /></span>
                        <div>
                            <strong>
                                {hasNoCoachSelection
                                    ? '선택한 강사가 없어요'
                                    : hasActiveFilter
                                    ? '조건에 맞는 일정이 없어요'
                                    : '이 기간에는 일정이 없어요'}
                            </strong>
                            <small>
                                {hasNoCoachSelection
                                    ? '강사 필터에서 한 명 이상 선택해 주세요.'
                                    : hasActiveFilter
                                    ? '필터를 초기화하면 전체 일정을 볼 수 있어요.'
                                    : '새 일정을 바로 등록할 수 있어요.'}
                            </small>
                        </div>
                        <button
                            type="button"
                            onClick={() => {
                                if (hasNoCoachSelection) {
                                    setFilters((current) => ({ ...current, coachMode: 'all', coachIds: [] }));
                                    return;
                                }
                                if (hasActiveFilter) {
                                    setFilters(DEFAULT_FILTERS);
                                    return;
                                }
                                handleCreateSlot({
                                    date: safeSelectedDay.format('YYYY-MM-DD'),
                                    startTime: getDefaultCreateTime(safeSelectedDay),
                                });
                            }}
                        >
                            {hasNoCoachSelection ? '모두 선택' : hasActiveFilter ? '필터 초기화' : '일정 등록'}
                        </button>
                    </div>
                )}
                </div>
                </div>
            </SmartSchedulePreviewProvider>
        </CoachColorProvider>
    );
};

export default SmartScheduleView;
